import Guacamole from 'guacamole-common-js';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { debounceTime, first } from 'rxjs/operators';
import { useParams } from 'react-router';
import { ButtonView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import {
    useActiveUserContext,
    useAppConfigContext,
    useAuthServiceContext,
    useModalApiContext,
    useVirtualMachineApiContext
} from '@vivli/core/infrastructure/context';
import { Subject, Subscription } from 'rxjs';
import { useTabFocusHook } from '@vivli/shared/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

// clientWidth/clientHeight on the root element are spec'd to return the viewport minus scrollbars,
// so they ignore the `body { min-width: 1320px }` floor in styles.scss and are unaffected by what
// we add to or remove from #display. document.body would report 1320 on a narrower window. (DEV-841)
const getViewportSize = () => ({
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight
});

// MS-RDPEDISP requires an even width and constrains both dimensions, so these are a guard against
// sending something guacd would refuse outright - not a judgement about what size is sensible.
//
// 200 is the protocol's own floor. Do NOT raise it to something that "looks reasonable": a viewport
// is far shorter than it is wide once browser chrome and the OS taskbar are subtracted, so a 1366x768
// screen leaves roughly 600px of height. Any floor above that pins every height to the floor, the
// "already at this size" check below then sees no change, and vertical resizing silently does
// nothing while horizontal keeps working. Both bounds are even, so clamping cannot reintroduce an
// odd value.
//
// The unit is whatever getRemoteSize yields: CSS pixels at devicePixelRatio 1 and above, physical
// pixels below it. 4096 clears every panel we realistically meet now that the ratio is capped -
// before the cap a 3008-wide retina Mac asked for 6016 and clamped here, which letterboxed roughly
// 900px of dead space beside an already unreadable desktop. Only a CSS viewport wider than 4096
// still clamps, and applyDisplayScale degrades that gracefully by fitting whatever the server gave
// us rather than distorting it. Raising the ceiling is a separate judgement about what guacd and the
// Server 2016 display driver will accept, so it has deliberately not been raised here.
// (DEV-1219, DEV-841)
const MIN_REMOTE_DIMENSION = 200;
const MAX_REMOTE_DIMENSION = 4096;

// how long we wait for the server's `size` instruction before recording a miss.
//
// raised from 2000, which was too tight to be safe. The round trip is not mostly network. guacd
// 1.5.3 throttles display updates to one per GUAC_RDP_DISP_UPDATE_INTERVAL and only flushes the
// pending one from the RDP client message loop, so a request can sit for most of that interval
// before it even leaves guacd. The guest then performs a real mode change - a Windows Server 2016
// display driver reallocating its surface costs hundreds of milliseconds, and over a second on a
// loaded VM - and only then does guacd allocate the new buffer and send `size` back through the
// websocket proxy. That budget is already past 2000, so the old value could score a perfectly
// healthy resize as a miss, and one miss used to condemn the session permanently. (DEV-841)
const RESIZE_ACK_TIMEOUT_MS = 4000;

// how close a new request has to be to one the server has already answered before we treat it as
// the same question. The guest is free to move our numbers: MS-RDPEDISP forces an even width, and
// the Server 2016 display driver picks the nearest entry from its own mode list rather than the
// exact size asked for - which is how a request for 1190 comes back as 1184. 8px is also well under
// a line of text, so declining to re-ask for something this close is invisible. (DEV-841)
const RESIZE_SETTLE_TOLERANCE_PX = 8;

// consecutive unanswered requests, at DISTINCT sizes, before we stop trusting the in-band path.
//
// A single miss is a weak signal by construction: the repeated-request case is suppressed before it
// reaches the wire by getResizeSkipReason, and a session that dropped inside the wait window is
// excluded by the isConnected check in handleResizeTimeout, so what is left is a guest that declined
// ONE particular size - which says nothing about the channel. 3 rather than 2 because two is
// reachable by one unlucky pair: a mode change stalled behind something else on the guest, plus a
// guacd flush that landed at the wrong end of its throttle interval. An image that genuinely has no
// display-update channel misses every single time, so it reaches 3 within a few seconds of
// dragging. (DEV-841)
const RESIZE_MISS_LIMIT = 3;

// how long resizes go through a reconnect after the miss limit is hit, before we probe in band
// again. This is what replaces the permanent latch. Long enough that a user dragging a window does
// not pay the probe repeatedly, short enough that a guest which recovers - a session that
// reconnected, a driver that came back - is not stuck in the reconnect path for the life of the
// tab. (DEV-841)
const DISPLAY_UPDATE_RETRY_AFTER_MS = 5 * 60 * 1000;

// pause before the single retry of a handshake that closed on us. not zero: the likeliest causes are
// a still-tearing-down RDP session on the guest and a transient rejection at the websocket proxy,
// and an immediate retry would hit both in the same state it just failed in. (DEV-841)
const CONNECT_RETRY_DELAY_MS = 1000;

const toRdpDimension = (value: number) => {
    const floored = Math.floor(value);
    const even = floored - (floored % 2);

    return Math.min(MAX_REMOTE_DIMENSION, Math.max(MIN_REMOTE_DIMENSION, even));
};

const getDevicePixelRatio = () => window.devicePixelRatio || 1;

// the multiplier we are willing to apply to the CSS viewport when asking the guest for a desktop.
// Equivalently: never ask for more remote pixels than EITHER the CSS viewport or the physical panel.
//
// The cap is the whole of DEV-1219, and it exists because the two directions of this ratio are
// different things wearing the same clothes.
//
// BELOW 1 the CSS viewport overstates the panel. At 80% browser zoom on a 1920x1080 screen the
// ratio is 0.8 and the viewport reports about 2400x1190, so asking for 2400x1190 remote pixels
// means Windows renders a desktop the browser then has to paint into 1920x952 physical ones:
// everything resampled, hairlines dropped, the whole desktop about 20% smaller than native. Scaling
// down by the ratio is a correctness guard against requesting pixels that do not exist, and that is
// what DEV-841 was really fixing. It is preserved here exactly.
//
// ABOVE 1 the ratio is the user's own magnification - a retina panel, Windows display scaling, or
// browser zoom in - and a CSS viewport is already physical pixels DIVIDED by it. Multiplying it
// back in cancels that choice out of the equation entirely, which is why DEV-841 left the VM
// identically sized at 100% and at 150% Windows scaling, and why a retina Mac got a desktop with
// four times the pixels and half-size text in the same physical space.
//
// We cannot push the magnification onto the guest instead. guacd parses the `dpi` argument into
// settings->resolution, which it reads ONLY to derive width/height when those are absent - we send
// both - and guac_rdp_push_settings writes neither DesktopScaleFactor nor DeviceScaleFactor, so
// Windows is never told to scale and always renders whatever pixel count we ask for at 100%.
// GUACAMOLE-1852 is the open request for that capability. Client-side is the only lever there is.
//
// The cost is sharpness above ratio 1: one remote pixel covers `ratio` device pixels, so retina
// becomes a clean 2:1 upscale - soft, the way every non-retina-aware application is, and the way
// this was before DEV-841. That trade is deliberate. A user-selectable magnification, including a
// native-sharp option that restores the uncapped multiply, is its own story. (DEV-1219)
const getRemoteSizeRatio = () => Math.min(getDevicePixelRatio(), 1);

// the single place a viewport becomes a number we are willing to put on the wire, so the connect
// path and the resize path always agree on what a given viewport means.
//
// viewportWidth/viewportHeight come back from the SAME measurement purely so the logging can show
// both. Reading document.documentElement a second time forces another layout and can legitimately
// disagree mid-drag, which would produce a log that contradicts the request it is explaining. The
// effective `ratio` rides along for the same reason - a log that shows the raw devicePixelRatio
// without the multiplier actually applied cannot explain the size next to it. (DEV-1219)
const getRemoteSize = () => {
    const viewport = getViewportSize();
    const ratio = getRemoteSizeRatio();

    return {
        width: toRdpDimension(viewport.width * ratio),
        height: toRdpDimension(viewport.height * ratio),
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        ratio
    };
};

const isWithinResizeTolerance = (a: number, b: number) => Math.abs(a - b) <= RESIZE_SETTLE_TOLERANCE_PX;

// PERMANENT instrumentation, not scratch diagnostics - do not strip these on the way to a PR.
//
// This path cannot be reproduced locally: it depends on the guest's mode list, on the VM's load at
// the moment of the request, and on the reporter's browser zoom. The console is the only instrument
// there is, and the previous round of this fix deleted its logging once it had calibrated - which is
// why the follow-up bug had to be diagnosed from source rather than from evidence. One prefix on
// every line so a user can be asked for "everything that says DEV-841" and it arrives complete.
const RESIZE_LOG_PREFIX = '[DEV-841 resize]';

const logResize = (event: string, detail: Record<string, unknown>) => console.info(`${RESIZE_LOG_PREFIX} ${event}`, detail);

const warnResize = (event: string, detail: Record<string, unknown>) => console.warn(`${RESIZE_LOG_PREFIX} ${event}`, detail);

// names a Guacamole.Status so a connection failure is readable in the console.
// Status.Code also carries the fromWebSocketCode function, hence the typeof filter.
const describeGuacStatus = (status): string => {
    if (!status) {
        return 'none';
    }

    const codes = Guacamole.Status.Code as unknown as Record<string, unknown>;
    const name = Object.keys(codes).find((key) => typeof codes[key] === 'number' && codes[key] === status.code);
    const hex = typeof status.code === 'number' ? `0x${status.code.toString(16).padStart(4, '0')}` : String(status.code);

    return `${name ?? 'UNKNOWN'} (${hex}) ${status.message ?? ''}`.trim();
};

const containerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'relative'
};

const displayStyle: CSSProperties = {
    ...containerStyle,
    cursor: 'none'
};

const messageContainerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    cursor: 'default',
    backgroundColor: 'white',
    zIndex: 1
};

const messageStyle: CSSProperties = {
    display: 'grid',
    rowGap: '15px'
};

enum GuacStateEnum {
    Idle,
    Connecting,
    Waiting,
    Connected,
    Disconnecting,
    Disconnected
}

enum GuacTunnelStateEnum {
    Connecting,
    Open,
    Closed,
    Unstable
}

export const VirtualMachinePortalFeature = () => {
    const { virtualMachineId, dataRequestDoi, userId } = useParams();
    const virtualMachineService = useVirtualMachineApiContext();
    const modalService = useModalApiContext();
    const configService = useAppConfigContext();
    const { hasPermission } = useActiveUserContext();
    const authService = useAuthServiceContext();
    const timeoutRef = useRef<any>();
    const handleTabFocus = useTabFocusHook();

    const resizeSubjectRef = useRef(new Subject<void>());
    const activitySubjectRef = useRef(new Subject<void>());
    const combinedGuacStateRef = useRef(new Subject<{ clientState?: GuacStateEnum; tunnelState?: GuacTunnelStateEnum }>());
    const guacClientRef = useRef<Guacamole.Client>(null);
    const guacTunnelRef = useRef<Guacamole.WebSocketTunnel>(null);
    const guacClientStateRef = useRef<number>(GuacStateEnum.Disconnected);
    const displayRef = useRef<HTMLDivElement>(null);
    const activeModalRef = useRef<string>(null);
    const activeGuacDisplayRef = useRef<HTMLElement>();
    const dismissModalTimeoutRef = useRef<any>();
    const lastGuacStatusRef = useRef<any>();
    const guacInitRef = useRef<boolean>(false);

    // refs rather than state throughout: the resize subscription is built once in the mount effect,
    // so it would close over an initial state value forever. (DEV-841)

    // the in-flight size request, non-null only between sendSize and either its settle or its
    // timeout. priorWidth/priorHeight are what the display measured when we asked, kept now purely
    // so the logging can show where we came from - they are no longer part of any decision, and that
    // demotion is the whole of the Layer 2 fix. sentAt is a monotonic clock reading at send, so every
    // line can carry a real elapsed time. (DEV-841)
    const pendingResizeRef = useRef<{ width: number; height: number; priorWidth: number; priorHeight: number; sentAt: number }>(null);
    const resizeAckTimeoutRef = useRef<any>();

    // THE RECONCILIATION MEMORY. What we last asked the server for, and what size the display
    // actually ended up at in response - whether that came back as a `size` instruction or as
    // silence. This is the ref that stops us asking a question the server has already answered.
    //
    // Its absence is what broke the previous version. A guest that settles on 2400x1184 when asked
    // for 2400x1190 has answered; but the viewport has not changed, so the next resize recomputed
    // 1190, saw the display at 1184, decided "different, send it", and asked again. That second
    // request moves nothing, so guacd emits no `size` at all, so the detector called it a failure -
    // forever, because nothing recorded that 1190 had already been refused. (DEV-841)
    const lastResizeSettleRef = useRef<{ requestedWidth: number; requestedHeight: number; achievedWidth: number; achievedHeight: number }>(
        null
    );

    // what the connect handshake asked for, so the FIRST size instruction of a session can seed the
    // memory above. nulled as soon as it is used. see the onresize handler. (DEV-841)
    const connectRequestRef = useRef<{ width: number; height: number }>(null);

    // consecutive misses at distinct sizes. any settle resets it, which is the recovery path the old
    // permanent latch had no equivalent of. (DEV-841)
    const resizeMissCountRef = useRef<number>(0);

    // wall-clock time until which resizes go through a reconnect instead of sendSize. 0 means the
    // in-band path is trusted. This replaces displayUpdateSupportedRef, which was latched false on
    // the first timeout and never set back for the mount - so one unlucky timeout brought back the
    // teardown-and-reconnect behaviour this ticket removed, for the life of the tab. (DEV-841)
    const displayUpdateUnsupportedUntilRef = useRef<number>(0);

    // the media query watching for a devicePixelRatio change that no window resize accompanies. see
    // watchDevicePixelRatio. (DEV-841)
    const devicePixelRatioQueryRef = useRef<MediaQueryList>(null);

    // the in-flight guac token request, so a teardown can cancel it before it opens a connection
    const tokenSubRef = useRef<Subscription>(null);

    // one retry per failure episode. latched on use and cleared by a successful connect, so a later
    // failure gets its own retry. it cannot spin: every reset requires a real working session first.
    const connectRetriedRef = useRef<boolean>(false);
    const connectRetryTimeoutRef = useRef<any>();

    // set only when the idle timeout actually disconnected us, so regaining focus does not reconnect
    // a session that was never dropped. see onTabFocus. (DEV-841)
    const idleDisconnectedRef = useRef<boolean>(false);

    const [disconnectedMsg, setDisconnectedMsg] = useState<string>(null);

    document.title = `Vivli - ${dataRequestDoi}`;

    const showOverlayMessage = (message: string, showLoading: boolean = true) => {
        activeModalRef.current && modalService.dismiss(activeModalRef.current);

        const modalComponent = showLoading ? (
            <LoadIndicatorCenteredView message={message} />
        ) : (
            <div style={messageContainerStyle}>{message.replace(/"/g, '')}</div>
        );

        activeModalRef.current = modalService.custom(modalComponent, {
            fullScreen: true
        });
    };

    const createGuacClient = () => {
        guacTunnelRef.current = new Guacamole.WebSocketTunnel(configService.guacUrl);
        guacClientRef.current = new Guacamole.Client(guacTunnelRef.current);
    };

    const isConnected = () => guacClientStateRef.current === GuacStateEnum.Connected && !!guacTunnelRef.current?.isConnected();

    // Renders the remote desktop at the right physical size, and is the counterpart to whatever
    // getRemoteSize asked for. The canvas is `remoteWidth` pixels wide; dividing the CSS viewport by
    // that gives the transform that lays it over exactly that much of the window. Below ratio 1 the
    // request was in physical pixels and this puts one remote pixel on one device pixel, which is
    // what keeps a zoomed-out window sharp. At or above 1 the request was the CSS viewport, so the
    // transform resolves to 1 and one remote pixel covers `devicePixelRatio` device pixels.
    // (DEV-1219, DEV-841)
    //
    // The SMALLER of the two axis ratios, so the whole desktop fits inside the window whatever size
    // the guest actually settled on. That is what structurally prevents the taskbar sitting below the
    // fold, which is the symptom this ticket started from, and it is upstream Guacamole's own fit
    // behaviour. Computed from what the server GAVE us rather than what we asked for, so a clamped or
    // rounded answer still produces a correctly fitted desktop - which is what makes declining to
    // re-ask for a refused size (getResizeSkipReason) invisible rather than a frozen wrong-sized
    // desktop.
    //
    // Display.scale() only writes a CSS transform and the bounds styles. It does not dispatch
    // onresize, so calling this from inside onresize cannot loop. transform-origin is "0 0" on the
    // Display's own element, so scaling cannot introduce a positional offset. (DEV-841)
    const applyDisplayScale = () => {
        const display = guacClientRef.current?.getDisplay();

        if (!display) {
            return;
        }

        const remoteWidth = display.getWidth();
        const remoteHeight = display.getHeight();

        // before the first size instruction the display is 0x0 and the ratio is meaningless
        if (!remoteWidth || !remoteHeight) {
            return;
        }

        const { width: viewportWidth, height: viewportHeight } = getViewportSize();

        display.scale(Math.min(viewportWidth / remoteWidth, viewportHeight / remoteHeight));
    };

    // @types/guacamole-common-js declares only sendMouseState(state). The runtime signature in
    // guacamole-common-js 1.5.0 is sendMouseState(state, applyDisplayScale), and that second argument
    // divides the coordinates by display.getScale() before they go on the wire.
    //
    // That is mandatory now, not cosmetic. Guacamole.Mouse reports CSS pixels relative to
    // document.body; the VM expects remote pixels. Those were the same number only while the display
    // scale was 1, and applyDisplayScale above is exactly what ends that. Without the flag every
    // click would land in the wrong place, worst at the far corners. It also fixes the client-side
    // cursor, which sendMouseState positions from the same coordinates.
    //
    // One narrow cast on the method rather than an `any` on the client ref, and called through the
    // instance so it keeps its receiver. (DEV-841)
    const sendScaledMouseState = (mouseState: Guacamole.Mouse.State) => {
        const sendWithDisplayScale = guacClientRef.current.sendMouseState as (
            state: Guacamole.Mouse.State,
            applyDisplayScale: boolean
        ) => void;

        sendWithDisplayScale.call(guacClientRef.current, mouseState, true);
    };

    // bound exactly once, from the mount effect. Guacamole 1.5.0 exposes no detach on either
    // Keyboard or Mouse, so rebinding per reconnect leaked a live handler pair every time and made
    // each keystroke reach the VM once per resize performed. these read guacClientRef/guacTunnelRef,
    // which createGuacClient reassigns in place, so one pair serves every reconnect. (DEV-841)
    const handleKeyBinding = () => {
        const keyboard = new Guacamole.Keyboard(document.body);

        const fixKeys = (keysym) => {
            // 65508 - Right Ctrl
            // 65507 - Left Ctrl
            // somehow Right Ctrl is not sent, so send Left Ctrl instead
            if (keysym === 65508) return 65507;
            return keysym;
        };

        keyboard.onkeydown = function (keysym) {
            if (!isConnected()) return;

            guacClientRef.current.sendKeyEvent(1, fixKeys(keysym));
        };
        keyboard.onkeyup = function (keysym) {
            if (!isConnected()) return;

            guacClientRef.current.sendKeyEvent(0, fixKeys(keysym));
            activitySubjectRef.current.next();
        };

        // Mouse
        const mouse = new Guacamole.Mouse(document.body);
        mouse.onmousemove = function (mouseState) {
            if (!isConnected()) return;

            sendScaledMouseState(mouseState);
            activitySubjectRef.current.next();
        };
        mouse.onmousedown = mouse.onmouseup = function (mouseState) {
            if (!isConnected()) return;

            sendScaledMouseState(mouseState);
            activitySubjectRef.current.next();
        };
    };

    const parentOnClickHandler = () => {
        displayRef.current.focus();
    };

    const getGuacToken = () => {
        //TODO PERMFE - redo with permissions+
        if (hasPermission(PermissionsEnum.DrActAdv)) {
            // login to VM as specific authorized user
            if (userId) {
                return virtualMachineService.getUserGuacToken(virtualMachineId, userId);
            }

            return virtualMachineService.getAdminGuacToken(virtualMachineId);
        }

        return virtualMachineService.getGuacToken(virtualMachineId);
    };

    const handleGuacConnect = () => {
        // the token fetch is async, so without holding the subscription a teardown that lands while
        // it is in flight cannot stop it: next() still runs and opens a websocket against a client we
        // already disconnected, leaving a connection nothing owns. handleDisconnect unsubscribes, and
        // every teardown path goes through it. (DEV-841)
        tokenSubRef.current?.unsubscribe();

        tokenSubRef.current = getGuacToken()
            .pipe(first())
            .subscribe({
                next: (token) => {
                    const { width, height, viewportWidth, viewportHeight, ratio } = getRemoteSize();

                    // recorded BEFORE connect(), so the session's first size instruction cannot beat
                    // it. connect() only opens a websocket, so nothing can arrive synchronously - but
                    // the ordering costs nothing and removes the question. (DEV-841)
                    connectRequestRef.current = { width, height };

                    // INERT in this connection, and kept only because it costs nothing. guacd reads
                    // dpi into settings->resolution purely to derive width/height when those are
                    // absent, and we always send both explicitly - so resolution is discarded and
                    // nothing downstream ever sees this number. It is NOT a scaling hint; see
                    // getRemoteSizeRatio for why the guest cannot be asked to scale at all.
                    //
                    // Derived from the effective ratio rather than the raw devicePixelRatio so that
                    // it describes the grid we are actually asking for, which is what makes it the
                    // right value if the handshake path ever starts to matter. Still rounded:
                    // guacd's guac_user_parse_args_int rejects trailing non-digits outright, and a
                    // raw ratio yields things like 76.80000000000001 at 80% zoom. (DEV-1219, DEV-841)
                    const dpi = Math.round(ratio * 96) || 96;

                    logResize('connect requesting', {
                        requested: `${width}x${height}`,
                        viewport: `${viewportWidth}x${viewportHeight}`,
                        devicePixelRatio: getDevicePixelRatio(),
                        effectiveRatio: ratio,
                        dpi
                    });

                    guacClientRef.current.connect('token=' + token + '&width=' + width + '&height=' + height + '&dpi=' + dpi);
                },
                error: (error) => {
                    showOverlayMessage(error, false);
                }
            });
    };

    const handleGuacConnected = () => {
        setDisconnectedMsg(null);

        // a working session earns the next failure its own retry
        connectRetriedRef.current = false;

        // every reconnect builds a new Guacamole.Client, and so a new display element. without this
        // the previous one stays in #display and covers the live one, which is what put the taskbar
        // off-screen and made the mouse look dead after a resize. must run before the assignment
        // below, which is what destroys the handle to the stale node. (DEV-841)
        removeGuacDisplay();

        activeGuacDisplayRef.current = guacClientRef.current.getDisplay().getElement();

        displayRef.current.appendChild(activeGuacDisplayRef.current);

        // a reconnect builds a new Display, which starts back at scale 1. onresize normally corrects
        // that the moment the new session's first size instruction lands, but this runs off a
        // debounced Connected state and so can be a second late - so reapply here too rather than
        // leave a window where the desktop is drawn at the wrong physical size. Idempotent.
        //
        // deliberately does NOT reset displayUpdateUnsupportedUntilRef: the fallback would then last
        // exactly one resize, and an image that genuinely has no display-update channel would
        // re-probe on every single resize forever. (DEV-841)
        applyDisplayScale();

        // cancel any dismissal still pending from a previous connect so rapid resizes don't stack timers
        clearTimeout(dismissModalTimeoutRef.current);

        const connectedModalId = activeModalRef.current;

        // wait to allow login to happen behind loading screen
        dismissModalTimeoutRef.current = setTimeout(() => {
            // only dismiss the overlay this connect came up under - a resize inside these 2s replaces
            // it, and modalService.dismiss(falsy) falls through to clearModals(). (DEV-841)
            if (!connectedModalId || activeModalRef.current !== connectedModalId) {
                return;
            }

            modalService.dismiss(connectedModalId);
            activeModalRef.current = null;
        }, 2000);
    };

    const handleGuacStateChange = () => {
        // close_tunnel() hands a Guacamole.Status to onerror immediately before it sets the tunnel
        // Closed. a deliberate disconnect closes with SUCCESS, which onerror skips, so only real
        // failures land here - and this is the only detail we get about one. (DEV-841)
        guacTunnelRef.current.onerror = (status) => {
            lastGuacStatusRef.current = status;
            console.error('Guacamole tunnel error:', describeGuacStatus(status));
        };

        guacClientRef.current.onerror = (status) => {
            lastGuacStatusRef.current = status;
            console.error('Guacamole client error:', describeGuacStatus(status));
        };

        // the server answers a size request by pushing a `size` instruction for the default layer,
        // which the Display turns into onresize. it fires for EVERY default-layer size instruction,
        // including the initial connect's and including ones whose dimensions have not changed, so
        // this must be idempotent and must never call sendSize - that would loop. bound here rather
        // than on connect because createGuacClient builds a new Display every reconnect, and because
        // this runs before the tunnel opens, so no size instruction can beat it. (DEV-841)
        //
        // The width/height ARGUMENTS are the source of truth here, not display.getWidth(). They are
        // the same values by the time this runs - Display.resize updates displayWidth/displayHeight
        // inside the same scheduled task, immediately before dispatching this callback - but reading
        // the arguments makes a future change to the flush order irrelevant.
        guacClientRef.current.getDisplay().onresize = (width: number, height: number) => {
            const pending = pendingResizeRef.current;

            if (!pending) {
                // Nothing of ours is in flight, so this is the initial connect's size or a
                // server-initiated change. Seed the reconciliation memory from the connect request,
                // once. If the guest clamped what the handshake asked for, that clamp is already
                // evidence about what it will and will not give us, and without capturing it the
                // FIRST in-band resize of every session asks for the same refused size and waits out
                // the full timeout for nothing. (DEV-841)
                const connectRequest = connectRequestRef.current;

                if (!connectRequest) {
                    // A size change the server made on its own, after the connect seed was consumed
                    // - the user changing the resolution inside the guest is the realistic case. The
                    // canvas has just changed size, so a scale computed for the previous one is now
                    // wrong in exactly the way this ticket is about: the desktop letterboxes or the
                    // taskbar goes below the fold, and every click is divided by a stale scale.
                    // There is nothing to reconcile here, but there is still a scale to refit.
                    // (DEV-841)
                    applyDisplayScale();

                    return;
                }

                connectRequestRef.current = null;

                lastResizeSettleRef.current = {
                    requestedWidth: connectRequest.width,
                    requestedHeight: connectRequest.height,
                    achievedWidth: width,
                    achievedHeight: height
                };

                applyDisplayScale();

                logResize('connect settled', {
                    requested: `${connectRequest.width}x${connectRequest.height}`,
                    achieved: `${width}x${height}`,
                    scale: guacClientRef.current.getDisplay().getScale()
                });

                return;
            }

            // RECONCILIATION, not acknowledgement, and this is the heart of the fix.
            //
            // guacd emits a default-layer `size` only when the remote surface actually changes, so
            // any size instruction arriving inside our wait window IS the server answering us -
            // including one that lands back on the dimensions we started from. The previous version
            // additionally required the numbers to differ from the pre-request size, which read a
            // clamped answer as "display-update is unsupported", latched the session into
            // reconnect-on-resize for good, and brought back the garbled desktop, the off-screen
            // taskbar and the dead mouse this ticket removed.
            //
            // Mistaking an unrelated size instruction for our answer is harmless under this model:
            // we would record the current size as what the server gave us for this request, and the
            // only consequence is that we decline to re-ask for that same size - which is what we
            // want regardless of who caused the instruction. (DEV-841)
            pendingResizeRef.current = null;
            clearTimeout(resizeAckTimeoutRef.current);

            lastResizeSettleRef.current = {
                requestedWidth: pending.width,
                requestedHeight: pending.height,
                achievedWidth: width,
                achievedHeight: height
            };

            // a settle proves the channel works, so whatever run of misses preceded it is no longer
            // evidence of anything. this is the recovery the old permanent latch could not express.
            resizeMissCountRef.current = 0;

            applyDisplayScale();

            logResize('settled', {
                requested: `${pending.width}x${pending.height}`,
                achieved: `${width}x${height}`,
                prior: `${pending.priorWidth}x${pending.priorHeight}`,
                elapsedMs: Math.round(performance.now() - pending.sentAt),
                scale: guacClientRef.current.getDisplay().getScale()
            });
        };

        guacTunnelRef.current.onstatechange = (tunnelState: GuacTunnelStateEnum) => {
            combinedGuacStateRef.current.next({ tunnelState, clientState: guacClientStateRef.current });
        };

        guacClientRef.current.onstatechange = (clientState: GuacStateEnum) => {
            // track client state changes since the guac client api does not have easy access to it
            guacClientStateRef.current = clientState;

            combinedGuacStateRef.current.next({ tunnelState: guacTunnelRef.current.state, clientState });
        };
    };

    const handleGuacInit = () => {
        lastGuacStatusRef.current = null; // a new attempt must not report the previous one's failure
        createGuacClient();
        handleGuacStateChange();
        handleGuacConnect();
    };

    // any teardown invalidates an in-flight size request. without this the ack timer fires seconds
    // later against a client that no longer exists and reconnects a session the idle timeout or a
    // tunnel error deliberately closed. handleDisconnect is the single choke point every teardown
    // path goes through - the idle timeout, every reconnect, and the effect cleanup. (DEV-841)
    const cancelPendingResize = () => {
        pendingResizeRef.current = null;
        clearTimeout(resizeAckTimeoutRef.current);

        // the reconciliation memory describes ONE Guacamole.Display talking to ONE RDP session.
        // Every teardown replaces both, and the new session can have a different mode list, so what
        // the old one refused is not evidence about the new one. The miss run and the unused connect
        // request go with it for the same reason.
        //
        // displayUpdateUnsupportedUntilRef deliberately does NOT reset here. handleResizeFallback
        // sets that window and then reconnects, and that reconnect comes straight back through this
        // function on the same call stack - clearing it here would undo the fallback in the very
        // tick it was engaged, and the session would flap between the two paths. (DEV-841)
        lastResizeSettleRef.current = null;
        resizeMissCountRef.current = 0;
        connectRequestRef.current = null;
    };

    const handleDisconnect = () => {
        cancelPendingResize();

        // stop an in-flight token fetch from connecting behind us, and drop any retry still queued
        tokenSubRef.current?.unsubscribe();
        tokenSubRef.current = null;
        clearTimeout(connectRetryTimeoutRef.current);

        // disconnect fully from guacamole
        guacTunnelRef.current.disconnect();
        guacClientRef.current.disconnect();
    };

    const handleGuacReconnect = () => {
        handleDisconnect();
        handleGuacInit();
    };

    const handleReconnect = () => {
        showOverlayMessage('Reconnecting...');
        handleGuacReconnect();
    };

    // Returns a short reason when a size request must NOT go on the wire, or null when it should.
    // Written as an early-return resolver rather than a chain of conditions inside the subscriber so
    // each reason carries its own explanation and its own log line - "nothing happened" is the
    // reported symptom, so telling "we chose not to ask" apart from "we asked and got silence" is
    // the difference between a readable console and another blind round. (DEV-841)
    const getResizeSkipReason = (width: number, height: number, currentWidth: number, currentHeight: number) => {
        // the clamp maps several viewports onto one remote size. asking for the size we already have
        // changes nothing on the guest, so guacd sends no size instruction at all.
        if (currentWidth === width && currentHeight === height) {
            return 'already at this size';
        }

        const settle = lastResizeSettleRef.current;

        if (!settle) {
            return null;
        }

        // the display has moved since the server last answered us - a reconnect, or a change the
        // server initiated - so what we learned then no longer describes where we are now
        if (settle.achievedWidth !== currentWidth || settle.achievedHeight !== currentHeight) {
            return null;
        }

        // We asked for this, or near enough; the server answered by putting the display where it is
        // now; and it has not moved since. Asking the same question again cannot produce a different
        // answer, and on a guest that answered by NOT moving it produces no size instruction at all -
        // which is exactly what the old detector scored as a failure, over and over, until it latched
        // the session into reconnect-on-resize. THIS IS THE LOOP-BREAKER.
        //
        // The tolerance applies only to the REQUESTED side; the achieved side is exact, because that
        // is a measurement of where we are rather than an estimate. That asymmetry is what makes the
        // rule safe - it can only suppress a request when the display provably has not moved since
        // the server last spoke. (DEV-841)
        if (isWithinResizeTolerance(settle.requestedWidth, width) && isWithinResizeTolerance(settle.requestedHeight, height)) {
            return 'server already answered this request';
        }

        return null;
    };

    const handleResizeFallback = () => {
        // sendSize is fire and forget: no ack, no promise, no error if the server ignores it, so a
        // silent wait window is the only symptom available. RESIZE_MISS_LIMIT distinct sizes in a row
        // going unanswered is the point at which "this image has no RDP Display Update channel" beats
        // the alternative explanations, because the repeated-request case is already suppressed by
        // getResizeSkipReason and a dropped session is already excluded by the isConnected check in
        // handleResizeTimeout.
        //
        // TIME-BOXED, NOT LATCHED. The latch is the regression this change is fixing: one unlucky run
        // of timeouts - a guest that would not take one particular height, a mode change stalled on a
        // loaded VM - condemned every later resize for the life of the mount to a teardown and
        // reconnect, which is the garbled desktop, the off-screen taskbar and the dead mouse coming
        // back. A window costs a genuinely unsupported image one probe per retry interval, and gives
        // a guest that recovers a way back in band.
        //
        // set BEFORE the reconnect below. handleGuacReconnect runs synchronously through
        // handleDisconnect into cancelPendingResize, which resets the miss run - so this ordering is
        // what makes that reset mean "start the next probe clean" rather than "discard the evidence
        // we just acted on". (DEV-841)
        displayUpdateUnsupportedUntilRef.current = Date.now() + DISPLAY_UPDATE_RETRY_AFTER_MS;
        resizeMissCountRef.current = 0;

        warnResize('display-update is not answering, falling back to reconnect on resize', {
            missesRequired: RESIZE_MISS_LIMIT,
            retryInBandAfterMs: DISPLAY_UPDATE_RETRY_AFTER_MS
        });

        showOverlayMessage('Resizing...');
        handleGuacReconnect();
    };

    // The wait window closed with no size instruction. On its own that is a weak signal, not proof -
    // so record what it taught us, count it, and let the limit decide when a run of them adds up to
    // something. (DEV-841)
    const handleResizeTimeout = () => {
        const pending = pendingResizeRef.current;

        // the display already settled, or a teardown cancelled us
        if (!pending) {
            return;
        }

        pendingResizeRef.current = null;

        // the session may have gone down inside the wait window. A request that was never going to be
        // answered because the tunnel closed says nothing about display-update, so it must not count
        // as a miss. (DEV-841)
        if (!isConnected()) {
            return;
        }

        const display = guacClientRef.current.getDisplay();
        const currentWidth = display.getWidth();
        const currentHeight = display.getHeight();

        // SILENCE IS ITSELF AN ANSWER. The guest was asked for this size and did not move, so it is
        // recorded exactly the way a real size instruction is recorded - requested size on one side,
        // the size we are actually sitting at on the other. That is what makes getResizeSkipReason
        // stop us asking the same unanswerable question on every subsequent resize. Without this half
        // the memory only ever learns from replies, the refused size is re-requested forever, and the
        // miss counter climbs to the limit on a single stuck height. (DEV-841)
        lastResizeSettleRef.current = {
            requestedWidth: pending.width,
            requestedHeight: pending.height,
            achievedWidth: currentWidth,
            achievedHeight: currentHeight
        };

        resizeMissCountRef.current = resizeMissCountRef.current + 1;

        warnResize('no size instruction within the wait window', {
            requested: `${pending.width}x${pending.height}`,
            display: `${currentWidth}x${currentHeight}`,
            prior: `${pending.priorWidth}x${pending.priorHeight}`,
            elapsedMs: Math.round(performance.now() - pending.sentAt),
            consecutiveMisses: resizeMissCountRef.current
        });

        // a miss below the limit is silent and free - no overlay, no teardown, nothing the user sees.
        // That is the point: the overwhelmingly likely cause of one miss is a guest already as close
        // to the requested size as it is willing to get, and applyDisplayScale means what is on
        // screen stays correctly scaled and fully visible regardless.
        if (resizeMissCountRef.current < RESIZE_MISS_LIMIT) {
            return;
        }

        handleResizeFallback();
    };

    const handleResizeDebounce = () => {
        resizeSubjectRef.current.next();
    };

    const handleWindowResize = () => {
        window.addEventListener('resize', handleResizeDebounce);
    };

    // devicePixelRatio is load-bearing for BOTH rendering and mouse coordinates whenever it is below
    // 1, because getRemoteSizeRatio then scales the request down by it and applyDisplayScale scales
    // the canvas back up to match - so a change in it has to reach the resize path even when the CSS
    // viewport does not change. Dragging the window to a second monitor with different OS scaling at
    // the same logical size does exactly that, and the failure would be silent: the desktop drawn at
    // the wrong physical size and every click landing off-target, with no resize event to correct
    // it. At or above 1 the ratio is capped and the request is the CSS viewport, so the listener
    // costs a no-op pass through the debounced subject rather than being wrong. (DEV-1219, DEV-841)
    //
    // window.resize already fires on browser zoom in every browser we support, so this is not the
    // main path - it exists because the hazard is introduced by this change. A media query can only
    // test one fixed value, so the listener re-arms itself against the new ratio each time it fires.
    // (DEV-841)
    const handleDevicePixelRatioChange = () => {
        watchDevicePixelRatio();

        // routed through the same debounced subject as a window resize, so the scale refit and any
        // size request go through exactly one code path
        resizeSubjectRef.current.next();
    };

    const watchDevicePixelRatio = () => {
        devicePixelRatioQueryRef.current?.removeEventListener('change', handleDevicePixelRatioChange);

        const query = window.matchMedia(`(resolution: ${getDevicePixelRatio()}dppx)`);

        query.addEventListener('change', handleDevicePixelRatioChange);
        devicePixelRatioQueryRef.current = query;
    };

    const unwatchDevicePixelRatio = () => {
        devicePixelRatioQueryRef.current?.removeEventListener('change', handleDevicePixelRatioChange);
        devicePixelRatioQueryRef.current = null;
    };

    const handleTokenRefresh = () => {
        authService.getTokenSilent().then(() => {
            // no action necessary, this will refresh the users session to keep it alive
        });
    };

    const onTabFocus = () => {
        if (timeoutRef.current) {
            // if the timeout still exists, we are still connected to guac
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            return;
        }

        // Only reconnect when the idle timeout actually disconnected us. This used to infer that
        // from timeoutRef being null, but null also means "we have never blurred" - and the portal
        // is opened with window.open, so a tab created in the background loads already hidden and
        // fires its FIRST visibilitychange as 'visible' with no preceding blur. That inference then
        // tore down a handshake still in progress, which surfaced as an intermittent
        // "Unable to access the Secure Research Environment" on a connection that was fine.
        // (DEV-841)
        if (!idleDisconnectedRef.current) {
            return;
        }

        idleDisconnectedRef.current = false;

        // the idle timeout really did disconnect us, so bring the session back now the tab is visible
        handleReconnect();
    };

    const onTabBlur = () => {
        const timeoutInMs = configService.guacTimeoutMinutes * 60000;
        timeoutRef.current = setTimeout(() => {
            handleDisconnect();

            // recorded rather than inferred, so onTabFocus can tell a real idle disconnect apart
            // from a tab that simply became visible for the first time
            idleDisconnectedRef.current = true;
            timeoutRef.current = null;
        }, timeoutInMs);
    };

    const removeGuacDisplay = () => {
        // clear the ref first so a second call is a no-op, and detach via the node's own parent so
        // this cannot throw once displayRef is null after unmount. (DEV-841)
        const staleDisplay = activeGuacDisplayRef.current;
        activeGuacDisplayRef.current = null;

        staleDisplay?.parentNode?.removeChild(staleDisplay);
    };

    const handleConnectionState = (clientState: GuacStateEnum, tunnelState: GuacTunnelStateEnum) => {
        if (clientState === GuacStateEnum.Connected) {
            handleGuacConnected();
        } else if (clientState === GuacStateEnum.Disconnected) {
            removeGuacDisplay();
            setDisconnectedMsg('You have been disconnected, please click the button below to reconnect.');
        } else if (tunnelState === GuacTunnelStateEnum.Closed) {
            // this branch needs clientState to be neither Connected nor Disconnected, so record
            // which state we were in alongside the failure status - otherwise a genuine connection
            // failure is indistinguishable from a race. (DEV-841)
            console.error(
                'Guacamole tunnel closed while clientState =',
                GuacStateEnum[clientState] ?? clientState,
                '- last status:',
                describeGuacStatus(lastGuacStatusRef.current)
            );

            // errors are not coming through correctly, subscribe to closed state to catch connection issues
            removeGuacDisplay();

            // the handshake closed on us. this is intermittent in practice and a second attempt
            // usually succeeds, so spend one before telling the user the environment is unreachable.
            // the latch is cleared by handleGuacConnected, so this is one retry per failure episode
            // rather than one per session, and it cannot spin because a reset needs a real connect.
            // (DEV-841)
            if (!connectRetriedRef.current) {
                connectRetriedRef.current = true;

                console.warn('Guacamole closed the connection during the handshake. Retrying once.');

                showOverlayMessage('Connecting...');
                clearTimeout(connectRetryTimeoutRef.current);
                connectRetryTimeoutRef.current = setTimeout(handleGuacReconnect, CONNECT_RETRY_DELAY_MS);

                return;
            }

            showOverlayMessage('Unable to access the Secure Research Environment. Please try again or contact support.', false);
        }
    };

    useEffect(() => {
        // force single instance only
        if (guacInitRef.current) return;
        guacInitRef.current = true;

        showOverlayMessage('Connecting...');

        handleTabFocus({
            onTabFocus,
            onTabBlur
        });

        // init first so guacTunnelRef/guacClientRef are populated before any input handler can fire
        handleGuacInit();
        handleKeyBinding();
        handleWindowResize();
        watchDevicePixelRatio();

        const resizeSub = resizeSubjectRef.current.pipe(debounceTime(500)).subscribe(() => {
            // only resize a live session. while disconnected - the idle timeout fired, the tunnel
            // errored, or the disconnected panel is waiting on a Reconnect click - reconnecting here
            // would silently revive the session and replace the message explaining why it went down.
            // checked after the debounce so it reflects the state at the moment we would act, and so
            // the wait timer below can never arm on a dead session. (DEV-841)
            if (!isConnected()) {
                return;
            }

            const { width, height, viewportWidth, viewportHeight, ratio } = getRemoteSize();

            // BEFORE any decision below, including the skips. The viewport has changed even when we
            // decline to ask the guest for a new size, and refitting the scale is the entire reason
            // that skip is invisible rather than a desktop left at the wrong size in the wrong box.
            // (DEV-841)
            applyDisplayScale();

            // the in-band path missed enough distinct sizes in a row to be worth doubting, so resizes
            // go through a reconnect until the window expires and we probe again
            if (Date.now() < displayUpdateUnsupportedUntilRef.current) {
                warnResize('reconnecting instead of resizing in place', {
                    requested: `${width}x${height}`,
                    retryInBandInMs: displayUpdateUnsupportedUntilRef.current - Date.now()
                });

                showOverlayMessage('Resizing...');
                handleGuacReconnect();
                return;
            }

            // read once and reuse for the skip check, the pending record and the log, so none of the
            // three can disagree about what the size was when we asked
            const display = guacClientRef.current.getDisplay();
            const priorWidth = display.getWidth();
            const priorHeight = display.getHeight();

            const skipReason = getResizeSkipReason(width, height, priorWidth, priorHeight);

            if (skipReason) {
                logResize('skipped', {
                    reason: skipReason,
                    requested: `${width}x${height}`,
                    display: `${priorWidth}x${priorHeight}`,
                    viewport: `${viewportWidth}x${viewportHeight}`,
                    scale: display.getScale()
                });

                return;
            }

            pendingResizeRef.current = { width, height, priorWidth, priorHeight, sentAt: performance.now() };

            logResize('requesting', {
                requested: `${width}x${height}`,
                display: `${priorWidth}x${priorHeight}`,
                viewport: `${viewportWidth}x${viewportHeight}`,
                devicePixelRatio: getDevicePixelRatio(),
                effectiveRatio: ratio
            });

            // armed BEFORE sendSize: onresize is dispatched from a scheduled task on flush, with no
            // ordering guarantee relative to sendSize returning, so the settle must never be able to
            // arrive before there is a timer to cancel
            clearTimeout(resizeAckTimeoutRef.current);
            resizeAckTimeoutRef.current = setTimeout(handleResizeTimeout, RESIZE_ACK_TIMEOUT_MS);

            // in-band: the websocket stays open and the desktop re-lays out in place, so there is no
            // overlay here. the overlay belongs to the fallback, which is a real reconnect.
            guacClientRef.current.sendSize(width, height);
        });

        const activitySub = activitySubjectRef.current.pipe(debounceTime(1000)).subscribe(() => {
            handleTokenRefresh();
        });

        const guacStateSub = combinedGuacStateRef.current.pipe(debounceTime(1000)).subscribe(({ clientState, tunnelState }) => {
            handleConnectionState(clientState, tunnelState);
        });

        return () => {
            window.removeEventListener('resize', handleResizeDebounce);
            unwatchDevicePixelRatio();
            resizeSub.unsubscribe();
            activitySub.unsubscribe();
            guacStateSub.unsubscribe();

            clearTimeout(dismissModalTimeoutRef.current);

            handleDisconnect();
        };
    }, []);

    return (
        <div style={containerStyle}>
            {disconnectedMsg && (
                <div style={messageContainerStyle}>
                    <div style={messageStyle}>
                        <div>{disconnectedMsg}</div>
                        <div>
                            <ButtonView onClick={handleReconnect}>Reconnect</ButtonView>
                        </div>
                    </div>
                </div>
            )}
            <div ref={displayRef} id="display" onClick={parentOnClickHandler} style={displayStyle} />
        </div>
    );
};
